import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const YoutubeSchema = z.object({
  url: z.string().trim().min(1).max(500),
});

function extractYoutubeId(input: string): string | null {
  const trimmed = input.trim();
  // Bare 11-char id
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const v = u.searchParams.get("v");
      if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;
      // /shorts/<id> or /embed/<id> or /live/<id>
      const m = u.pathname.match(/\/(shorts|embed|live)\/([a-zA-Z0-9_-]{11})/);
      if (m) return m[2];
    }
  } catch {
    /* not a URL */
  }
  return null;
}

export const fetchYoutubeTranscript = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => YoutubeSchema.parse(input))
  .handler(async ({ data }) => {
    const id = extractYoutubeId(data.url);
    if (!id) throw new Error("That doesn't look like a YouTube URL. Paste a link like https://youtube.com/watch?v=…");

    const { YoutubeTranscript } = await import("youtube-transcript");
    let parts: { text: string }[];
    try {
      parts = await YoutubeTranscript.fetchTranscript(id);
    } catch (err) {
      const msg = (err as Error).message || "";
      if (/disabled|not available|transcript/i.test(msg)) {
        throw new Error("This video has no captions available. Try a different video or upload the audio file instead.");
      }
      throw new Error("Couldn't fetch that video's transcript. Make sure the video is public and has captions.");
    }
    const text = parts
      .map((p) => p.text)
      .join(" ")
      .replace(/\s+/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .trim();

    if (!text) throw new Error("That video's transcript came back empty.");
    return { text, videoId: id, charCount: text.length };
  });

const AudioSchema = z.object({
  // base64-encoded audio/video bytes (no data: prefix)
  data: z.string().min(10).max(40_000_000), // ~30 MB base64
  mimeType: z
    .string()
    .regex(/^(audio|video)\/[a-zA-Z0-9.+-]+$/)
    .max(100),
  filename: z.string().max(255).optional(),
});

export const transcribeAudio = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => AudioSchema.parse(input))
  .handler(async ({ data }) => {
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");

    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    let result;
    try {
      result = await generateText({
        model,
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text:
                  "Transcribe this lecture/audio recording verbatim into clean, paragraph-formatted text. " +
                  "Use blank lines between distinct topics or natural breaks. Do not summarize, do not add commentary, do not add timestamps. " +
                  "If multiple speakers are clearly distinguishable, label them as Speaker 1, Speaker 2, etc. Otherwise output continuous prose.",
              },
              {
                type: "file",
                data: data.data,
                mediaType: data.mimeType,
              },
            ],
          },
        ],
      });
    } catch (err) {
      const msg = (err as Error).message || "";
      if (/quota|credit|402|429/i.test(msg)) {
        throw new Error("AI service is busy or out of credits. Try again in a minute.");
      }
      throw new Error("Couldn't transcribe that file. Try a shorter clip or a different format.");
    }

    const text = (result.text || "").trim();
    if (!text) throw new Error("Transcription came back empty. The audio may be silent or unsupported.");
    return { text, charCount: text.length };
  });
